/**
 * React Query hooks for community operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCommunities,
  getCommunity,
  createCommunity,
  updateCommunity,
  deleteCommunity,
  importCommunity,
} from '../api/communities';

/** Query key factory for communities. */
export const communityKeys = {
  all: ['communities'],
  detail: (id) => ['communities', id],
};

/**
 * Fetch all communities.
 * @param {number} [skip=0]
 * @param {number} [limit=100]
 */
export function useCommunities(skip = 0, limit = 100) {
  return useQuery({
    queryKey: [...communityKeys.all, { skip, limit }],
    queryFn: () => getCommunities(skip, limit),
  });
}

/**
 * Fetch a single community with full nested data.
 * @param {string|null} id - Community UUID
 */
export function useCommunity(id) {
  return useQuery({
    queryKey: communityKeys.detail(id),
    queryFn: () => getCommunity(id),
    enabled: !!id,
  });
}

/**
 * Create a new community.
 * Invalidates the community list on success.
 */
export function useCreateCommunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCommunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
    },
  });
}

/**
 * Update an existing community.
 * @param {string} communityId
 */
export function useUpdateCommunity(communityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateCommunity(communityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
      queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) });
    },
  });
}

/**
 * Delete a community.
 */
export function useDeleteCommunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCommunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
    },
  });
}

/**
 * Import a full community from JSON export.
 */
export function useImportCommunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: importCommunity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.all });
    },
  });
}
